import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import type { MqttLiveMessage } from "../../types/mqtt";

const SCROLL_STICKY_THRESHOLD_PX = 48;

function formatPayload(raw: string) {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function MqttLiveFeed({
  messages,
  connected,
  error,
  topicPattern,
}: {
  messages: MqttLiveMessage[];
  connected: boolean;
  error: string | null;
  topicPattern: string | null;
}) {
  const [paused, setPaused] = useState(false);
  const [frozenMessages, setFrozenMessages] = useState<MqttLiveMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToTopRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);

  const displayMessages = paused ? frozenMessages : messages;
  const pendingCount = paused ? Math.max(0, messages.length - frozenMessages.length) : 0;

  useEffect(() => {
    setPaused(false);
    setFrozenMessages([]);
    stickToTopRef.current = true;
    prevMessageCountRef.current = 0;
    prevScrollHeightRef.current = 0;
  }, [topicPattern]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (paused) {
      prevScrollHeightRef.current = el.scrollHeight;
      return;
    }

    const prevCount = prevMessageCountRef.current;
    const nextCount = messages.length;

    if (nextCount > prevCount) {
      if (stickToTopRef.current) {
        el.scrollTop = 0;
      } else if (prevScrollHeightRef.current > 0) {
        const delta = el.scrollHeight - prevScrollHeightRef.current;
        if (delta > 0) el.scrollTop += delta;
      }
    }

    prevMessageCountRef.current = nextCount;
    prevScrollHeightRef.current = el.scrollHeight;
  }, [messages, paused]);

  function togglePause() {
    if (paused) {
      setPaused(false);
      setFrozenMessages([]);
      stickToTopRef.current = true;
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      });
      return;
    }
    setFrozenMessages(messages);
    setPaused(true);
  }

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    stickToTopRef.current = el.scrollTop <= SCROLL_STICKY_THRESHOLD_PX;
  }

  if (!topicPattern) {
    return (
      <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
        Select a topic from the catalog to start live sniffing.
      </p>
    );
  }

  const reversed = [...displayMessages].reverse();

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={[
            "inline-flex rounded-full px-2 py-0.5 font-medium",
            paused
              ? "bg-text-tertiary/15 text-text-secondary"
              : connected
                ? "bg-status-ok/15 text-status-ok"
                : "bg-status-warn/15 text-status-warn",
          ].join(" ")}
          style={{ fontSize: "var(--font-micro)" }}
        >
          {paused ? "Paused" : connected ? "Live" : "Connecting…"}
        </span>
        <span className="font-mono text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
          {topicPattern}
        </span>
        <button
          type="button"
          onClick={togglePause}
          className={[
            "ml-auto inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-medium transition-colors",
            paused
              ? "border-accent bg-accent/10 text-accent"
              : "border-border-subtle bg-page text-text-primary hover:border-accent hover:text-accent",
          ].join(" ")}
          style={{ fontSize: "var(--font-micro)" }}
        >
          {paused ? (
            <>
              <Play size={12} strokeWidth={2} />
              Resume
              {pendingCount > 0 ? ` (+${pendingCount})` : ""}
            </>
          ) : (
            <>
              <Pause size={12} strokeWidth={2} />
              Pause
            </>
          )}
        </button>
      </div>

      {error && !paused && (
        <p className="mb-2 text-status-error" style={{ fontSize: "var(--font-micro)" }}>
          {error}
        </p>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain"
      >
        {reversed.length === 0 ? (
          <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            {paused ? "Feed paused — resume to continue." : "Waiting for messages…"}
          </p>
        ) : (
          reversed.map((msg, i) => (
            <div
              key={`${msg.ts}-${msg.topic}-${i}`}
              className="rounded-md border border-border-subtle bg-page p-2"
            >
              <div
                className="mb-1 flex flex-wrap gap-x-3 text-text-secondary"
                style={{ fontSize: "var(--font-micro)" }}
              >
                <span>{new Date(msg.ts).toLocaleTimeString()}</span>
                <span className="font-mono">{msg.topic}</span>
              </div>
              {msg.user_properties && (
                <div className="mb-1 font-mono text-text-secondary" style={{ fontSize: "10px" }}>
                  {Object.entries(msg.user_properties)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(" · ")}
                </div>
              )}
              <pre
                className="overflow-x-auto font-mono text-text-primary"
                style={{ fontSize: "10px" }}
              >
                {formatPayload(msg.payload)}
              </pre>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
